<template>
  <ShareModes
    :modes="modes"
    :fixed-mode="mode"
  >
    <template #link>
      <ShareAiDraftPanel
        platform="facebook"
        :ai-endpoint="aiEndpoint"
        :ai-body="aiBody"
        :language="language"
        :landing-page-url="landingPageUrl"
        :partner-name="partnerName"
        :initial-text="initialText"
        :copy-label="$t('copy_post')"
        open-href="https://facebook.com"
        :open-label="$t('open_facebook')"
        hide-intro
      >
        <template
          v-if="previewImageUrl"
          #media
        >
          <div class="relative">
            <span class="absolute left-3 top-3 flex items-center gap-2 rounded bg-gray-900/70 px-2.5 py-1 text-xs font-semibold text-white">
              <Icon icon="eye" />
              <span>{{ $t('preview') }}</span>
            </span>
            <img
              :src="previewImageUrl"
              alt="Link preview image"
              class="max-h-[28rem] w-full rounded-t object-cover"
            />
          </div>
        </template>
      </ShareAiDraftPanel>
    </template>

    <template #conversation>
      <ShareSteps>
        <ShareStep
          :number="1"
          :title="$t('facebook_copy_post_title')"
          :description="$t('share_ai_assistant_intro')"
        >
          <ShareAiDraftPanel
            platform="facebook-conversation"
            :ai-endpoint="aiEndpoint"
            :ai-body="aiBody"
            :language="language"
            :landing-page-url="landingPageUrl"
            :partner-name="partnerName"
            :copy-label="$t('copy_post')"
            open-href="https://facebook.com"
            :open-label="$t('open_facebook')"
            hide-intro
          />
        </ShareStep>

        <ShareStep
          :number="2"
          :title="$t('facebook_download_image_title')"
          :description="$t('facebook_download_image_description')"
        >
          <ShareImageDownload
            :media="media"
            preferred-aspect-ratio="horizontal"
          />
        </ShareStep>

        <ShareStep
          :number="3"
          :title="$t('facebook_copy_link_title')"
          :description="$t('facebook_copy_link_description')"
        >
          <ShareCopyLink
            :landing-page-url="landingPageUrl"
            open-href="https://facebook.com"
            :open-label="$t('open_facebook')"
          />
        </ShareStep>
      </ShareSteps>
    </template>
  </ShareModes>
</template>

<script setup lang="ts">
import type { LandingPage } from '~/types/landing-page'
import type { ShareMode } from '~/types/share'

const props = defineProps<{
  aiEndpoint: string
  aiBody?: Record<string, unknown>
  mode?: 'link' | 'conversation'
  language: string
  landingPageUrl: string
  partnerName?: string
  initialText?: string | null
  media?: LandingPage['media'] | null
  // The image Facebook will show for the link, where it isn't one of the media
  linkPreviewImageUrl?: string | null
}>()

const { t } = useI18n()

const modes: ShareMode[] = [
  {
    value: 'link',
    icon: 'link',
    title: t('facebook_drive_traffic_title'),
    description: t('facebook_drive_traffic_description'),
    features: [
      t('facebook_drive_traffic_feature_1'),
      t('facebook_drive_traffic_feature_2'),
      t('facebook_drive_traffic_feature_3')
    ],
    classes: {
      card: 'hover:border-primary-300 hover:bg-primary-50/50',
      icon: 'bg-primary-50 text-primary-500',
      accent: 'text-primary-500'
    }
  },
  {
    value: 'conversation',
    icon: 'message-smile',
    title: t('facebook_start_conversation_title'),
    description: t('facebook_start_conversation_description'),
    features: [
      t('facebook_start_conversation_feature_1'),
      t('facebook_start_conversation_feature_2'),
      t('facebook_start_conversation_feature_3')
    ],
    classes: {
      card: 'hover:border-violet-300 hover:bg-violet-50/50',
      icon: 'bg-violet-50 text-violet-600',
      accent: 'text-violet-600'
    }
  }
]

const previewImageUrl = computed(() => props.linkPreviewImageUrl || props.media?.social_horizontal_image_url || props.media?.header_background_image_url)
</script>
